import * as React from "react";
import { PricingTiers } from "../commerce";
import { apiService } from "../../services";
import type { CustomerProfile, UserInfo } from "../../services";
import { useSiteMetadata } from "../../hooks/useSiteMetadata";
import DashboardSkeleton from "./DashboardSkeleton";
import UserDownloads from "./UserDownloads";
import { BookCover } from "../common";
import BookDescription from "./BookDescription";
import WhatsIncluded from "./WhatsIncluded";
import ReaderMapSection from "./readermap/ReaderMapSection";
import { SectionNav, type NavSection } from "@multiagentbook/ui";
import PremiumSamples from "./PremiumSamples";
import { UpgradePrompt } from "../commerce";
import { AuthProviders } from "../auth";
import {
  Acknowledgement,
  Modal,
  Announcement,
  Changelog,
  ConvinceYourBoss,
  TrustedBy,
  Testimonials,
  AboutAuthors,
  Faqs,
} from "../common";
import {
  expertTestimonials,
  readerTestimonials,
} from "@multiagentbook/shared";
import { LoadingSpinner } from "../ui";
import { useTranslation } from "react-i18next";

const printSellers = [
  {
    name: 'Amazon',
    logo: '/images/sellers/amazon.svg',
    url: 'https://www.amazon.com/dp/B0G2BCQQJY',
  },
  {
    name: 'Bookshop',
    logo: '/images/sellers/bookshop.svg',
    logoHeight: 'h-5',
    url: 'https://bookshop.org/p/books/designing-multi-agent-systems-principles-patterns-and-implementation-for-ai-agents-victor-dibia/bb726b791e9a546e?ean=9798993101200&next=t',
  },
  {
    name: 'Barnes & Noble',
    logo: '/images/sellers/barnesnoble.svg',
    logoHeight: 'h-5',
    url: 'https://www.barnesandnoble.com/w/designing-multi-agent-systems-victor-dibia/1149126926?ean=9798993101200',
  },
];

// Lazy load Stripe component only when needed
const UpgradeCheckout = React.lazy(() => import("../commerce/UpgradeCheckout"));

interface UserDownload {
  id: string;
  type: "bookpdf" | "bookepub" | "samplecode" | "bonus" | "template";
  title: string;
  description: string;
  changelog: string;
  version: string;
  fileSize: number;
  fileName: string;
  updatedAt: string;
}

interface User {
  displayName: string | null;
  email: string | null;
  emailVerified: boolean;
  isAnonymous: boolean;
  metadata: any;
  photoURL: string | null;
  userID: string;
}

interface DashboardProps {
  user: User | null; // Allow anonymous users
}

const Dashboard: React.FC<DashboardProps> = ({ user }) => {
  const { t } = useTranslation("dashboard");
  const { t: tHome } = useTranslation("home");
  const { t: tCommon } = useTranslation("common");
  const { title } = useSiteMetadata();
  const [customerProfile, setCustomerProfile] =
    React.useState<CustomerProfile | null>(null);
  const [userInfo, setUserInfo] = React.useState<UserInfo | null>(null);
  const [downloads, setDownloads] = React.useState<UserDownload[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [showLoginModal, setShowLoginModal] = React.useState(false);
  const [showUpgradeCheckout, setShowUpgradeCheckout] = React.useState(false);
  const [upgradeCheckoutSecret, setUpgradeCheckoutSecret] = React.useState<
    string | null
  >(null);

  // Fetch user data on component mount - only when user is available and authenticated
  React.useEffect(() => {
    if (!user?.userID) return; // Don't fetch if no authenticated user

    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch auth status first
        const authData = await apiService.getAuthStatus();
        setUserInfo(authData);

        // Try to fetch customer profile (may not exist for new users)
        try {
          const profileData = await apiService.getCustomerProfile();
          setCustomerProfile(profileData);

          // Fetch user's available downloads based on their tier
          try {
            const downloadsData = await apiService.getUserDownloads();
            setDownloads(downloadsData);
          } catch (downloadsError) {
            setDownloads([]);
          }
        } catch (profileError) {
          setCustomerProfile(null);
          setDownloads([]);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load user data"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []); // Only re-run if user ID changes

  // Check tier status from customer data
  const customerTier = React.useMemo(() => {
    return customerProfile?.tier || "none";
  }, [customerProfile?.tier]);

  const hasActiveSubscription = React.useMemo(() => {
    return customerTier !== "none";
  }, [customerTier]);

  const getUserTierDisplay = React.useMemo(() => {
    if (customerProfile) {
      switch (customerProfile.tier) {
        case "essential":
          return t("tiers.essential");
        case "professional":
          return t("tiers.professional");
        case "enterprise":
          return t("tiers.enterprise");
        case "none":
        default:
          return t("tiers.noActivePurchases");
      }
    }
    return t("tiers.loading");
  }, [customerProfile?.tier, t]);

  const getMainTitle = () => {
    return tHome("hero.title");
  };

  // Get personalized greeting for customers with purchases - moved up to be always called
  const getCustomerGreeting = React.useMemo(() => {
    // Use real user data if available from backend
    if (userInfo?.customer?.displayName) {
      return t("greeting.welcomeBack", { name: userInfo.customer.displayName });
    }
    // Fallback to Firebase user data
    if (user?.displayName) {
      return t("greeting.welcomeBack", { name: user.displayName });
    }
    // Default greeting with email if available
    if (userInfo?.email || user?.email) {
      const email = userInfo?.email || user?.email;
      const firstName = email?.split("@")[0];
      return t("greeting.welcomeBack", { name: firstName });
    }
    return t("greeting.welcomeBackGeneric");
  }, [
    userInfo?.customer?.displayName,
    userInfo?.email,
    user?.displayName,
    user?.email,
    t,
  ]);

  const getUserWelcomeMessage = React.useMemo(() => {
    // Anonymous user - no welcome message
    if (!user) {
      return null;
    }

    // Get user's display name
    let userName = "";
    if (userInfo?.customer?.displayName) {
      userName = userInfo.customer.displayName;
    } else if (user.displayName) {
      userName = user.displayName;
    } else if (userInfo?.email || user.email) {
      const email = userInfo?.email || user.email;
      userName = email?.split("@")[0] || "";
    }

    return userName
      ? t("greeting.welcomeChoosePlan", { name: userName })
      : t("greeting.welcomeChoosePlanGeneric");
  }, [
    user,
    userInfo?.customer?.displayName,
    userInfo?.email,
    user?.displayName,
    user?.email,
    t,
  ]);

  const handlePurchaseClick = async (tier: string) => {
    // Handle upgrade to Professional tier from Premium Samples section
    if (tier === "professional" && customerTier === "essential") {
      // Use the same upgrade flow as UpgradePrompt
      await handleUpgradeClick();
    } else {
      // For non-upgrade purchases (shouldn't happen in current flow)
      console.warn("Direct purchase not implemented, tier:", tier);
    }
  };

  const handleUpgradeClick = async () => {
    try {
      // Create upgrade checkout session
      const checkoutData = await apiService.createUpgradeCheckout("embedded");

      if (checkoutData.client_secret) {
        // Use embedded checkout modal
        setUpgradeCheckoutSecret(checkoutData.client_secret);
        setShowUpgradeCheckout(true);
      } else if (checkoutData.checkout_url) {
        // Fallback to hosted checkout
        window.location.href = checkoutData.checkout_url;
      }
    } catch (error) {
      console.error("Failed to initiate upgrade:", error);
      // Could show an error message to the user
    }
  };

  const handleUpgradeSuccess = () => {
    setShowUpgradeCheckout(false);
    setUpgradeCheckoutSecret(null);
    // Reload page to show updated tier
    window.location.reload();
  };

  const handleUpgradeCancel = () => {
    setShowUpgradeCheckout(false);
    setUpgradeCheckoutSecret(null);
  };

  const handleLoginClick = () => {
    setShowLoginModal(true);
  };

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    // User will be updated via auth hook, component will re-render
  };

  const handleLoginCancel = () => {
    setShowLoginModal(false);
  };

  const LoginPrompt = () => {
    if (user) return null;

    return (
      <div className="mt-4 ">
        <p className="text-sm text-secondary">
          {tCommon("auth.alreadyHaveAccount")}{" "}
          <button
            onClick={handleLoginClick}
            className="text-accent font-medium hover:underline"
          >
            {tCommon("auth.signIn")}
          </button>
        </p>
        <ConvinceYourBoss className="mt-2" />
      </div>
    );
  };

  const AuthorCredibility = () => (
    <div className="bg-secondary/50 border border-tertiary rounded-lg p-6">
      <p className="text-secondary italic mb-3">
        {tHome("credibility.quote")}
      </p>
      <p className="text-sm font-semibold text-primary">— {tHome("credibility.author")}</p>
      <div className="mt-4 pt-4 border-t border-tertiary">
        <p className="text-sm text-secondary">
          {tHome("credibility.authorDescription")}
        </p>
      </div>
    </div>
  );

  // Show loading state with personalized greeting
  if (loading) {
    const userName = user
      ? user.displayName || (user.email ? user.email.split("@")[0] : undefined)
      : undefined;
    return <DashboardSkeleton userName={userName} />;
  }

  // Show error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-red-800 font-medium mb-2">
          {t("errors.failedToLoad")}
        </h3>
        <p className="text-red-600 text-sm mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
        >
          {tCommon("buttons.retry")}
        </button>
      </div>
    );
  }

  // Show PricingTiers for anonymous users or users without subscription
  if (!user || !hasActiveSubscription) {
    const navSections: NavSection[] = [
      { id: "inside-the-book", label: tHome("nav.insideTheBook") },
      { id: "testimonials", label: tHome("nav.testimonials") },
      { id: "about", label: tHome("nav.about") },
      { id: "faq", label: tHome("nav.faq") },
    ]
    return (
      <div>
        <Announcement />
        <SectionNav sections={navSections} firstScrollsToTop />
        {/* Mobile Layout */}
        <div className="lg:hidden">
          <div className="mb-8 text-center">
            <div className="flex justify-center mb-6">
              <BookCover size="mobile" />
            </div>
            <h1 className="text-4xl font-bold mb-2 text-primary">
              {getMainTitle()}
            </h1>
            {getUserWelcomeMessage && (
              <p className="text-lg text-accent font-medium mb-4">
                {getUserWelcomeMessage}
              </p>
            )}
            <BookDescription className="text-xl text-secondary mb-6 max-w-2xl mx-auto" />

            {/* Login Prompt */}
            <LoginPrompt />
          </div>

          {/* Mobile Pricing - Priority */}
          <div className="mb-8">
            <PricingTiers
              checkoutMode="embedded"
              userEmail={user?.email || ""}
            />
          </div>

          {/* Inside the Book - Mobile */}
          <section id="inside-the-book" className="scroll-mt-20 mb-8">
            <WhatsIncluded />
          </section>

          {/* Around the World (Trusted By + Reader Map) - Mobile */}
          <section id="around-the-world" className="scroll-mt-20 mb-8">
            <h2 className="text-2xl font-bold tracking-tight text-primary mb-4">
              {tHome("sections.aroundTheWorld")}
            </h2>
            <TrustedBy className="mt-4 mb-6" sellers={printSellers} />
            <ReaderMapSection />
          </section>

          {/* Premium Samples - Mobile */}
          <section id="premium-samples" className="scroll-mt-20 mb-8">
            <PremiumSamples
              hasActiveSubscription={false}
              customerTier="none"
              onPurchaseClick={handlePurchaseClick}
              onLoginClick={handleLoginClick}
            />
          </section>

          {/* What's New (Changelog) - Mobile */}
          <section id="whats-new" className="scroll-mt-20 mb-8">
            <Changelog user={user} />
          </section>

          {/* Testimonials - Mobile */}
          <section id="testimonials" className="scroll-mt-20 mb-8">
            <h2 className="text-2xl font-bold tracking-tight text-primary mb-2">
              {tHome("sections.testimonials")}
            </h2>
            <Testimonials testimonials={expertTestimonials} variant="experts" topDivider={false} />
            <Testimonials testimonials={readerTestimonials} variant="readers" />
          </section>

          {/* About the Author - Mobile */}
          <section id="about" className="scroll-mt-20 mb-8">
            <AboutAuthors />
          </section>

          {/* FAQ - Mobile */}
          <section id="faq" className="scroll-mt-20 mb-8">
            <Faqs />
            <Acknowledgement />
          </section>

          {/* Mobile Login Prompt - Remove duplicate */}
          {/* <LoginPrompt isMobile={true} /> */}
        </div>

        {/* Desktop Layout - New Structure */}
        <div className="hidden lg:block">
          {/* Top Section: Left Intro + Right Payment */}
          <div className="flex gap-8 items-start mb-12">
            {/* Left Side - Product Introduction */}
            <div className="flex-1">
              <div className="flex items-start gap-6 mb-8">
                <div className="flex-1">
                  <h1 className="text-5xl font-bold mb-3 text-primary">
                    {getMainTitle()}
                  </h1>
                  {getUserWelcomeMessage && (
                    <p className="text-xl text-accent font-medium mb-4">
                      {getUserWelcomeMessage}
                    </p>
                  )}
                  <BookDescription />

                  {/* Login Prompt */}
                  <LoginPrompt />
                </div>
                <div className="flex-shrink-0">
                  <BookCover size="desktop" />
                </div>
              </div>

              {/* Inside the Book */}
              <section id="inside-the-book" className="scroll-mt-20">
                <WhatsIncluded />
              </section>
            </div>

            {/* Right Side - Compact Pricing */}
            <div className="w-80 flex-shrink-0">
              <div className="sticky top-8">
                <PricingTiers
                  checkoutMode="embedded"
                  userEmail={user?.email || ""}
                  compact={true}
                />
              </div>
            </div>
          </div>

          {/* Around the World (Trusted By + Reader Map) - Desktop (full width) */}
          <section id="around-the-world" className="scroll-mt-20 mt-12 mb-12">
            <h2 className="text-3xl font-bold tracking-tight text-primary mb-4 sm:text-4xl">
              {tHome("sections.aroundTheWorld")}
            </h2>
            <TrustedBy className="mt-4 mb-6" sellers={printSellers} />
            <ReaderMapSection />
          </section>

          {/* Premium Samples - Desktop */}
          <section id="premium-samples" className="scroll-mt-20 mt-12 mb-12">
            <PremiumSamples
              hasActiveSubscription={false}
              customerTier="none"
              onPurchaseClick={handlePurchaseClick}
              onLoginClick={handleLoginClick}
            />
          </section>

          {/* What's New (Changelog) - Desktop */}
          <section id="whats-new" className="scroll-mt-20 mt-12 mb-12">
            <Changelog user={user} />
          </section>

          {/* Testimonials - Desktop */}
          <section id="testimonials" className="scroll-mt-20 mt-12 mb-12">
            <h2 className="text-3xl font-bold tracking-tight text-primary mb-2 sm:text-4xl">
              {tHome("sections.testimonials")}
            </h2>
            <Testimonials testimonials={expertTestimonials} variant="experts" topDivider={false} />
            <Testimonials testimonials={readerTestimonials} variant="readers" />
          </section>

          {/* About the Author - Desktop */}
          <section id="about" className="scroll-mt-20 mt-12 mb-12">
            <AboutAuthors />
          </section>

          {/* FAQ - Desktop */}
          <section id="faq" className="scroll-mt-20 mt-12 mb-12">
            <Faqs />
            <Acknowledgement />
          </section>
        </div>

        {/* Login Modal */}
        {showLoginModal && (
          <Modal
            isOpen={showLoginModal}
            onClose={handleLoginCancel}
            title={tCommon("auth.signIn")}
            size="md"
          >
            <div className="p-6">
              <AuthProviders
                onSuccess={handleLoginSuccess}
                onCancel={handleLoginCancel}
                showCancel={true}
                inline={true}
                description={tCommon("auth.signInToAccess")}
              />
            </div>
          </Modal>
        )}
      </div>
    );
  }

  // Full dashboard for customers with purchases
  const customerDashboard = (
    <div>
      <Announcement />
      <div className="mb-6">
        <h1 className="text-4xl font-bold mb-2 text-primary">
          {getCustomerGreeting}
        </h1>
        <p className="text-secondary mb-2">
          {t("customerDashboard.manageAccess")}
        </p>
        <div className="inline-block bg-accent/10 text-accent px-3 py-1 rounded-lg text-sm font-medium">
          {getUserTierDisplay} {t("customerDashboard.customer")}
        </div>
      </div>

      {/* Downloads Section */}
      <UserDownloads
        downloads={downloads}
        hasActiveSubscription={hasActiveSubscription}
        customerTier={customerTier}
        onPurchaseClick={handlePurchaseClick}
      />

      {/* Upgrade Prompt - Only for Essential and Professional customers */}
      {(customerTier === "essential" || customerTier === "professional") && (
        <UpgradePrompt
          customerTier={customerTier}
          onUpgradeClick={handleUpgradeClick}
        />
      )}

      {/* Premium Samples - Authenticated Users */}
      <div className="mt-8">
        <PremiumSamples
          hasActiveSubscription={hasActiveSubscription}
          customerTier={customerTier}
          onPurchaseClick={handlePurchaseClick}
          onLoginClick={handleLoginClick}
          downloads={downloads}
        />
      </div>

      {/* Changelog for customers */}
      <div className="mt-8">
        <Changelog user={user} />
      </div>

      {/* Upgrade Checkout Modal */}
      {showUpgradeCheckout && upgradeCheckoutSecret && (
        <Modal
          isOpen={showUpgradeCheckout}
          onClose={handleUpgradeCancel}
          title={t("checkout.secureCheckout")}
          size="xl"
        >
          <React.Suspense
            fallback={<LoadingSpinner size="lg" text={t("checkout.loading")} />}
          >
            <UpgradeCheckout
              clientSecret={upgradeCheckoutSecret}
              onSuccess={handleUpgradeSuccess}
              onCancel={handleUpgradeCancel}
            />
          </React.Suspense>
        </Modal>
      )}
    </div>
  );

  // Return the appropriate content
  return <>{customerDashboard}</>;
};

export default Dashboard;
